import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { Collapsible } from "@/app/components/ui/collapsible";
import { FaqSection } from "@/app/components/seo-support";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { EN_ROUTE_FAQS } from "@/app/seo-content-en";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "@/app/pages/Docs/QuickStartShared";

const SAMPLE_RESULT = `{
  "request_id": "req_xxx",
  "status": "SUCCEEDED",
  "result_package": {
    "request_id": "req_xxx",
    "responder_id": "responder_xxx",
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "ok",
    "output": {
      "summary": "..."
    },
    "timing": {
      "elapsed_ms": 123
    }
  }
}`;

const SAMPLE_ERROR = `{
  "request_id": "req_xxx",
  "status": "FAILED",
  "result_package": {
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "error",
    "error": {
      "code": "AUTH_TOKEN_INVALID",
      "message": "task token is missing or invalid"
    }
  }
}`;

export default function EnglishQuickStartCaller() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <QuickStartHero
        role="Caller · 5 min · EN"
        roleColor="#A3E635"
        titleTop="Connect one"
        titleBottom="real Hotline call"
        lead="Start from the published delexec-ops package, run one local Hotline call, and inspect one real result_package. The source install path is kept later for contributors."
        meta={["Local Mode", "No Platform required", "Supervisor HTTP"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">What this page gets you to</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">From zero to one cross-process capability call</h2>
          <ul className="mt-4 grid gap-2 text-sm font-bold text-zinc-800 sm:grid-cols-2">
            <li>· Start the local supervisor</li>
            <li>· Register a local Caller identity</li>
            <li>· Mount the built-in example Hotline</li>
            <li>· Use run-example to submit one local request</li>
            <li>· Read the shared result_package shape</li>
          </ul>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="Official path: install the CLI and bootstrap"
            intent="New users should start from the published @delexec/ops package. No superproject clone or monorepo context is required for the first local call."
          >
            <CodeBlock language="bash" caption="Any directory">{`npm install -g @delexec/ops
delexec-ops bootstrap --email you@example.com`}</CodeBlock>

            <Note label="Expected">
              These two commands install the CLI and start the local runtime. Use
              <code className="font-mono"> delexec-ops run-example </code>
              afterwards to verify the minimum Caller loop is working.
            </Note>

            <Collapsible title="Source install / Contributor path">
              <div className="space-y-2 text-sm text-zinc-600">
                <p>If you need to modify the CLI source or contribute:</p>
                <pre className="bg-zinc-100 p-3 rounded text-xs overflow-x-auto">
{`git clone --recursive https://github.com/hejiajiudeeyu/delegated-execution-dev.git
cd delegated-execution-dev
corepack pnpm install

cd repos/client
npm install`}
                </pre>
              </div>
            </Collapsible>
          </Step>

          <Step
            num="02"
            title="Source breakdown: start the supervisor"
            intent="If you are contributing code or debugging HTTP details, start the supervisor from source. First-time users can stay with delexec-ops bootstrap above."
          >
            <CodeBlock language="bash">{`export DELEXEC_HOME="$HOME/.delexec-quickstart-en"
export OPS_PORT_SUPERVISOR=8179

DELEXEC_HOME="$DELEXEC_HOME" \
OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \
npm run ops -- start`}</CodeBlock>

            <Note variant="warn" label="Warning">
              This runs in the foreground. Open a second terminal for the remaining steps. Once you see
              <code className="font-mono"> supervisor listening on 127.0.0.1:8179 </code>
              the runtime is ready.
            </Note>
          </Step>

          <Step
            num="03"
            title="Initialize the first session"
            intent="The first supervisor boot needs setup. After that, every admin request is scoped by the X-Ops-Session token."
          >
            <CodeBlock language="bash">{`BASE="http://127.0.0.1:${`$`}{OPS_PORT_SUPERVISOR:-8079}"
curl -s -X POST "$BASE/setup" -H 'content-type: application/json' -d '{}'
SESSION=$(curl -s -X POST "$BASE/auth/session/setup" \
  -H 'content-type: application/json' \
  -d '{"passphrase":"client-localtest-123"}' | jq -r '.token')
echo "$SESSION"
export OPS_SESSION="$SESSION"`}</CodeBlock>

            <Note label="Tip">
              If you prefer the CLI path, the same runtime is also documented in
              <code className="font-mono"> repos/client/docs/current/guides/local-mode-onboarding.md</code>.
            </Note>
          </Step>

          <Step
            num="04"
            title="Register a local Caller"
            intent="Even in Local Mode the protocol keeps Caller identity explicit. This step writes a local account into your DELEXEC_HOME runtime state."
          >
            <CodeBlock language="bash" caption="CLI">{`npm run ops -- auth register --local --email you@example.com`}</CodeBlock>

            <CodeBlock language="bash" caption="HTTP">{`curl -s -X POST "$BASE/auth/register-caller" \
  -H 'content-type: application/json' \
  -H "X-Ops-Session: $OPS_SESSION" \
  -d '{ "contact_email": "you@example.com" }'`}</CodeBlock>
          </Step>

          <Step
            num="05"
            title="Mount the built-in example Hotline"
            intent="Local Mode lets the same machine act as both Caller and Responder. That keeps the first verification loop small before you integrate any real capability."
          >
            <CodeBlock language="bash">{`curl -s -X POST "$BASE/responder/enable" \
  -H 'content-type: application/json' \
  -H "X-Ops-Session: $OPS_SESSION" \
  -d '{}'

curl -s -X POST "$BASE/responder/hotlines/example" \
  -H 'content-type: application/json' \
  -H "X-Ops-Session: $OPS_SESSION" \
  -d '{}'`}</CodeBlock>
          </Step>

          <Step
            num="06"
            title="List the local catalog"
            intent="Catalog is the key Caller-side API. It defines which Hotlines are currently available to the agent runtime."
          >
            <CodeBlock language="bash">{`curl -s "$BASE/catalog/hotlines" \
  -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>

            <Note label="Expected">
              You should see <code className="font-mono">local.delegated-execution.workspace-summary.v1</code> in the list.
            </Note>
          </Step>

          <Step
            num="07"
            title="Submit one real request"
            intent="At this point the runtime is live. The next request simulates the first external capability call an agent would make."
          >
            <CodeBlock language="bash" caption="HTTP">{`REQ=$(curl -s -X POST "$BASE/requests/example" \
  -H 'content-type: application/json' \
  -H "X-Ops-Session: $OPS_SESSION" \
  -d '{}')
echo "$REQ"
REQUEST_ID=$(echo "$REQ" | jq -r '.request_id')`}</CodeBlock>

            <CodeBlock language="bash" caption="CLI">{`npm run ops -- run-example --text "summarize my workspace"`}</CodeBlock>
          </Step>

          <Step
            num="08"
            title="Fetch the result and inspect result_package"
            intent="The protocol-level result envelope is the main thing to learn. Once your agent understands one result_package, it can reuse that outer shape across many Hotlines."
          >
            <CodeBlock language="bash">{`curl -s "$BASE/requests/$REQUEST_ID/result" \
  -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>

            <CodeBlock language="json" caption="Successful shape">{SAMPLE_RESULT}</CodeBlock>
            <CodeBlock language="json" caption="One common failure shape">{SAMPLE_ERROR}</CodeBlock>

            <div className="border-2 border-black bg-white/80 p-4 text-sm font-bold text-zinc-800">
              <p>Fields your agent should inspect first:</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                <li>· <code className="font-mono">status</code>: request lifecycle status</li>
                <li>· <code className="font-mono">result_package.status</code>: protocol-level success or error</li>
                <li>· <code className="font-mono">hotline_id</code>: the exact capability version you called</li>
                <li>· <code className="font-mono">output</code>: the capability-specific payload</li>
                <li>· <code className="font-mono">timing.elapsed_ms</code>: runtime duration for observability and pricing</li>
              </ul>
            </div>
          </Step>

          <Step
            num="09"
            title="OPC #0 production rehearsal: make one paid call after recharge with one CLI command"
            intent="This is not required for the local quick start. It is the public production rehearsal path: register a hosted Platform Caller, ask the operator to recharge that tenant, then let delexec-ops call-hotline handle billing consent, dispatch, result polling, and settlement evidence."
          >
            <CodeBlock language="bash" caption="Register Caller, configure the public relay runtime, and hand tenant_id to the operator">{`PLATFORM="https://callanything.xyz/platform"
RELAY="https://callanything.xyz/relay"
CALLER_EMAIL="caller-opc0"@"example.com"
export DELEXEC_HOME="$HOME/.delexec-caller-public-en"
export OPS_PORT_SUPERVISOR=8279
export OPS_PORT_CALLER=8281
export TRANSPORT_TYPE="relay_http"
export TRANSPORT_BASE_URL="$RELAY"

# delexec-ops auth register calls $PLATFORM/v1/users/register and stores the API key locally.
CALLER_JSON=$(delexec-ops auth register \
  --platform "$PLATFORM" \
  --email "$CALLER_EMAIL")
echo "$CALLER_JSON" | jq

CALLER_API_KEY=$(echo "$CALLER_JSON" | jq -r '.api_key')
CALLER_TENANT_ID=$(echo "$CALLER_JSON" | jq -r '.user_id')
export CALLER_API_KEY CALLER_TENANT_ID

# Run this in a second terminal, or keep it alive with nohup. The Caller
# controller consumes the result and reports the billing-settlement event.
nohup env \
  DELEXEC_HOME="$DELEXEC_HOME" \
  OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \
  OPS_PORT_CALLER="$OPS_PORT_CALLER" \
  TRANSPORT_TYPE="relay_http" \
  TRANSPORT_BASE_URL="$RELAY" \
  delexec-ops start \
  > "$DELEXEC_HOME/caller-public-relay.log" 2>&1 &

CALLER_BASE="http://127.0.0.1:$OPS_PORT_CALLER"

# Send CALLER_TENANT_ID to the operator. Continue after tenant creation and manual recharge.
curl -s "$PLATFORM/v1/tenants/me/balance" \
  -H "Authorization: Bearer $CALLER_API_KEY" | jq`}</CodeBlock>

            <CodeBlock language="bash" caption="Select an approved Hotline and make the paid call with one CLI command">{`HOTLINE_ID="your.namespace.tool-name.v1"
RESPONDER_ID="responder_xxx"
REQUEST_ID="req_opc0_$(date +%s)"
MAX_CHARGE_CENTS=500

curl -s "$PLATFORM/v1/catalog/hotlines/$HOTLINE_ID" | jq

CALL_RESULT=$(delexec-ops call-hotline \
  --platform "$PLATFORM" \
  --caller-base-url "$CALLER_BASE" \
  --request-id "$REQUEST_ID" \
  --responder-id "$RESPONDER_ID" \
  --hotline-id "$HOTLINE_ID" \
  --text "Summarize this paid public relay request." \
  --max-charge-cents "$MAX_CHARGE_CENTS" \
  --poll-interval-ms 2000 \
  --timeout-ms 60000)

echo "$CALL_RESULT" | tee /tmp/delexec-call-hotline.json | jq
echo "$CALL_RESULT" | jq '{request_id, task_token_present, task_token_claims, delivery_meta, dispatch, inbox, result, events, balance, ledger}'`}</CodeBlock>

            <CodeBlock language="bash" caption="Drop down to the low-level shape only when debugging">{`# call-hotline already wraps these steps:
# /v1/tokens/task
# /v1/requests/$REQUEST_ID/delivery-meta
# /controller/requests
# /controller/requests/$REQUEST_ID/dispatch
# $RELAY/v1/messages/send
# /controller/inbox/pull
# /controller/requests/$REQUEST_ID/result
# /v1/tenants/me/balance
# /v1/tenants/me/ledger
# /v1/requests/$REQUEST_ID/events
echo "$CALL_RESULT" | jq '{task_token_claims, delivery_meta, dispatch, inbox, result, events, balance, ledger}'`}</CodeBlock>

            <Note variant="warn" label="Acceptance">
              A paid rehearsal is done when <code className="font-mono">call-hotline</code> exits 0 and returns
              <code className="font-mono">task_token_claims</code>, <code className="font-mono">delivery_meta</code>,
              <code className="font-mono">dispatch</code>, <code className="font-mono">result</code>, <code className="font-mono">events</code>,
              <code className="font-mono">balance</code>, and <code className="font-mono">ledger</code>; and
              <code className="font-mono">/v1/tenants/me/balance</code> plus
              <code className="font-mono">/v1/tenants/me/ledger</code> reconcile against
              <code className="font-mono">max_charge_cents</code> and actual usage. The low-level APIs remain visible for diagnostics.
            </Note>
          </Step>
        </div>

        <div className="mt-12 border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">What to do next</h2>
          <p className="mt-3 text-sm font-bold text-zinc-900 sm:text-base">
            You now have the minimum Caller-side loop. From here, either expand the set of Hotlines your agent can call or switch to the seller-side path and publish one yourself.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="border-4 border-black bg-white/90 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">Expand the agent surface</p>
              <p className="mt-2 text-sm font-bold text-black">
                Open the Console to see discovery, approvals, and runtime history from the local catalog.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/console" label="Open Console" />
                <NextCta href="/marketplace" label="Browse Marketplace" tone="white" />
              </div>
            </div>
            <div className="border-4 border-black bg-white/90 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">Publish a capability</p>
              <p className="mt-2 text-sm font-bold text-black">
                The same supervisor can also host a Responder. Switch to the seller-side path to publish your own Hotline.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/en/docs/quick-start-responder/" label="Responder Quick Start" tone="blue" />
                <NextCta href="/en/docs/" label="Docs overview" tone="white" />
              </div>
            </div>
          </div>
        </div>

        <FaqSection
          eyebrow="Caller FAQ"
          title="Caller Quick Start common questions"
          intro="These are the three questions most likely to block the first caller-side integration, and they are also emitted as English FAQ schema for answer engines."
          items={EN_ROUTE_FAQS["/en/docs/quick-start-caller/"]}
          accent="#A3E635"
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
